import { sameCompanyFaq } from "@/lib/brand";
import { createFileRoute } from "@tanstack/react-router";
import { hourlyMinimumFaq } from "@/lib/booking-minimums";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-limo-1.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-limo-party.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryStarlight from "@/assets/sprinter-starlight-blue.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryBar from "@/assets/sprinter-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryEntry from "@/assets/sprinter-entry.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/sprinter-limo-atlanta";
const PAGE_TITLE = "Sprinter Limo Atlanta | Mercedes-Benz Sprinter Limousine Service";
const PAGE_DESC =
  "Atlanta's premier Mercedes-Benz Sprinter Limo service. Luxury Sprinter limos for nights out, weddings, corporate, and group transfers. 14 passengers, chauffeured. Call (678) 431-7222.";

const faqs = [
  {
    q: "How much does a Sprinter limo cost in Atlanta?",
    a: "Sprinter limo rates in Atlanta start from $250/hr, with custom quotes given upon request, or flat-rate pricing for airport transfers and point-to-point trips. Final pricing depends on the day, route, and any extras you request. Request a quote for an exact number.",
  },
  hourlyMinimumFaq({ transfers: true }),
  sameCompanyFaq(),
  {
    q: "How many people fit in a Mercedes-Benz Sprinter Limo?",
    a: "Our Mercedes-Benz Sprinter Limos seat up to 14 passengers comfortably in lounge-style configurations with wraparound leather seating, starlight headliner, premium sound, and a wet bar, perfect for groups that want to ride together instead of splitting across multiple sedans.",
  },
  {
    q: "What's the difference between a Sprinter limo and a regular limo?",
    a: "A Sprinter limo is built on the Mercedes-Benz Sprinter chassis, which gives you stand-up headroom, a much wider cabin, and a smoother, quieter ride than a traditional stretch limousine. It's the modern luxury limo, what wedding parties, executives, and bachelor/bachelorette groups in Atlanta book today.",
  },
  {
    q: "Can I book a Sprinter limo for a wedding or bachelorette party?",
    a: "Yes, weddings, bachelor and bachelorette parties, proposals, anniversaries, and birthdays are some of the most-booked occasions for our Sprinter Limos. We coordinate timing with your planner and provide a chauffeur dressed to match the formality of the event.",
  },
  {
    q: "Do you offer Sprinter limo service from Hartsfield-Jackson (ATL)?",
    a: "Absolutely. We do private Sprinter Limo transfers from ATL, PDK, FTY, RYY, and LZU to any Atlanta hotel, venue, or residence. Live flight tracking and meet-and-greet at baggage claim are standard.",
  },
  {
    q: "How far in advance should I book a Sprinter limo in Atlanta?",
    a: "We recommend 2 to 4 weeks for weekends, weddings and major events, including Masters week, the Super Bowl, Final Four and conventions. Midweek bookings may be possible with 24 to 48 hours' notice. Text me your date and I'll check personally.",
  },
  {
    q: "What's actually inside the Sprinter Limousine?",
    a: "Wraparound leather lounge seating for up to 14, a fiber-optic starlight headliner with color-changing LED accents, a wet bar with ice wells and stemware, a premium sound system with Bluetooth, USB charging at every seat, tinted privacy glass, and stand-up headroom through the center aisle. Rear cargo stays separate so luggage never rides in the cabin.",
  },
  {
    q: "Can we drink in the Sprinter limo?",
    a: "Passengers 21+ may drink in the cabin. The bar is stocked with ice and glassware; it is BYOB, so you bring your own drinks. We do not provide or sell alcohol. Georgia law prohibits open containers for the chauffeur and in the front compartment, the cabin is fully partitioned.",
  },
  {
    q: "How does a bachelorette or night-out itinerary usually run?",
    a: "A typical Atlanta night out is a 5-hour block: 7:00pm residence pickup, dinner drop in Buckhead or West Midtown, chauffeur waits on-site, 10:00pm move to the venue, then final returns at midnight. The vehicle stays with you the whole night, no waiting on rides between stops.",
  },
  {
    q: "Will a Sprinter limo fit at my venue?",
    a: "Our Sprinters are roughly 22 to 24 feet long and 9.5 feet tall. They clear many porte-cochères but can't enter a parking deck with 7-foot clearance. Text me the venue and I'll confirm the drop point and staging area.",
  },
  {
    q: "Do you do Atlanta brewery, winery, and North Georgia day trips?",
    a: "Regularly. Popular runs are the West Midtown and Chamblee brewery loops, the Dahlonega wine trail (about 1h15m each way), and Blue Ridge cabin weekends. These book as hourly blocks with the chauffeur staying with the group all day.",
  },
  {
    q: "Is the Sprinter limo available for photo shoots, tours, and film production?",
    a: "Yes. The cabin photographs well and doubles as a mobile green room. We work with touring artists, production companies, and photographers on hourly and day-rate bookings, with unbranded vehicles and NDA-bound chauffeurs on request.",
  },
];

export const Route = createFileRoute("/sprinter-limo-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "sprinter limo atlanta, mercedes sprinter limo, sprinter van limo, sprinter limo service, sprinter limo bus atlanta, luxury sprinter limo, mercedes-benz sprinter limousine atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Sprinter Limo Service",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Sprinter Limousine Service",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Buckhead" },
              { type: "City", name: "Midtown Atlanta" },
              { type: "City", name: "Sandy Springs" },
              { type: "City", name: "Alpharetta" },
              { type: "Airport", name: "Hartsfield-Jackson Atlanta International Airport (ATL)" },
              { type: "Airport", name: "DeKalb-Peachtree Airport (PDK)" },
            ],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(faqs)),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Atlanta • Mercedes-Benz Sprinter Limo"
      h1="Sprinter Limo Service in Atlanta"
      intro="You've planned the evening. Let my family handle the driving. Our Mercedes-Benz Sprinter Limousine seats up to 14, with lounge seating, starlight lighting, premium sound and your own professional chauffeur."
      heroImage={heroImage}
      heroAlt="Mercedes-Benz Sprinter Limo exterior in Atlanta, luxury Sprinter limousine service"
      midImage={midImage}
      midAlt="Interior of Mercedes-Benz Sprinter Limo with lounge seating and ambient lighting for Atlanta groups"
      sectionOneTitle="Your Group, Together"
      sectionOneBody={[
        "We own and operate our own vehicles, with custom-built interiors and full-time professional chauffeurs. Your Sprinter arrives detailed and ready for your group.",
        "Stand-up headroom, leather lounge seating, ambient lighting and premium audio give you room to settle in. Weddings, bachelorette weekends and executive transfers all feel easier together.",
        "Tell me your stops, whether it's a wedding, a 12-person dinner, a night across Atlanta or an ATL arrival. I'll help you plan the pickup.",
      ]}
      bulletsTitle="What Comes With Your Sprinter"
      bulletsIntro="One vehicle and one chauffeur, with the details arranged before pickup."
      bullets={[
        "Mercedes-Benz Sprinter Limousines, seats up to 14",
        "Lounge-style leather, starlight headliner, premium sound",
        "Full-time professional chauffeurs",
        "Wet bar, ice, glassware, and complimentary water",
        "Live flight tracking on every airport transfer",
        "Hourly, as-directed, and point-to-point service",
        "Fully licensed, insured, and TSA-compliant operation",
      ]}
      coverageTitle="Sprinter Limo Coverage Across Metro Atlanta"
      coverageIntro="Door-to-door Sprinter Limousine service throughout the city, suburbs, and every Atlanta-area airport."
      coverage={[
        "Buckhead",
        "Midtown Atlanta",
        "Downtown Atlanta",
        "Sandy Springs",
        "Alpharetta",
        "Dunwoody",
        "Vinings & Smyrna",
        "Marietta",
        "Brookhaven",
        "Roswell",
        "Decatur",
        "ATL, PDK, FTY, RYY & LZU",
      ]}
      routeTable={{
        title: "Popular Sprinter Limo Routes & Itineraries",
        intro:
          "The trips Atlanta books our Sprinter Limousines for most, with realistic drive times so you can build a schedule that actually holds.",
        rows: [
          { route: "Buckhead → Downtown night out", distance: "~8 mi", time: "15–30 min", notes: "Peachtree Rd staging; venue drop points confirmed in advance." },
          { route: "Hotel block → Wedding venue (in-town)", distance: "5–15 mi", time: "20–40 min", notes: "Guest transfer loops, typically 3–4 runs across the evening." },
          { route: "Atlanta → Chateau Elan (Braselton)", distance: "~45 mi", time: "50–75 min", notes: "I-85 N; wedding and golf weekend staple." },
          { route: "Atlanta → Dahlonega wine trail", distance: "~65 mi", time: "1h 15m–1h 30m", notes: "GA-400 N; full-day hourly booking with 3–4 winery stops." },
          { route: "Atlanta → Blue Ridge cabins", distance: "~95 mi", time: "1h 45m–2h 15m", notes: "GA-515; mountain grades, allow extra on return in the dark." },
          { route: "ATL Airport → Buckhead hotel", distance: "~17 mi", time: "30–45 min", notes: "Group arrival with full luggage in the rear cargo bay." },
          { route: "Atlanta → The Battery / Truist Park", distance: "13–20 mi", time: "25–45 min", notes: "Arrive 90 minutes before first pitch; designated staging lot." },
          { route: "Brewery loop (West Midtown + Chamblee)", distance: "~25 mi total", time: "4–5 hr block", notes: "3–4 stops with the chauffeur waiting on-site between each." },
        ],
        footnote:
          "Drive times are from our own trip history. Hourly bookings keep the vehicle with you between stops.",
      }}
      detailSections={[
        {
          title: "Inside the Mercedes-Benz Sprinter Limousine",
          body: [
            "This is not a stretched sedan. The Sprinter chassis gives you a wide, tall cabin you can stand and move in, the reason Atlanta groups stopped booking traditional stretch limos.",
          ],
          list: [
            "12–14 passengers in wraparound leather lounge seating.",
            "Fiber-optic starlight headliner with color-selectable LED accent lighting.",
            "Wet bar with ice wells, stemware, and chilled storage.",
            "Premium sound with Bluetooth streaming and passenger volume control.",
            "USB charging at every seat and tinted privacy glass throughout.",
            "Separate rear cargo bay, luggage and coolers never ride in the cabin.",
            "Stand-up center aisle height and a low step-in entry, so formalwear stays intact.",
          ],
        },
        {
          title: "Occasion Playbooks",
          list: [
            "Weddings, bridal party transfers, private couple's transfer between ceremony and reception, and guest loops to the hotel block.",
            "Bachelor & bachelorette, 5-hour evening block, multi-venue, chauffeur waits on-site between stops.",
            "Corporate & client entertainment, team transport to dinners, Truist Park suites, and conference venues in one vehicle.",
            "Concerts & sports, State Farm Arena, Mercedes-Benz Stadium, Truist Park; drop and staged pickup so nobody hunts for a ride afterward.",
            "Proms & milestone birthdays, chaperone-friendly bookings with parent contact and a fixed itinerary.",
            "Airport group arrivals, 12–14 passengers plus full checked luggage in a single transfer.",
          ],
        },
        {
          title: "Practical Details Before You Book",
          list: [
            "Dimensions: roughly 22–24 ft long, 9.5 ft tall, clears most porte-cochères, will not enter 7-ft-clearance parking decks.",
            "Custom quotes given upon request; airport and point-to-point trips are quoted flat.",
            "Alcohol: permitted for passengers 21+ in the partitioned cabin; BYOB, the bar is set with glassware and ice; we do not provide or sell alcohol.",
            "Lead time: 2–4 weeks for weekend nights, weddings, and event weeks; 24–48 hours is often workable midweek.",
            "Venue clearance: send the address and we confirm the drop point and staging area in advance.",
          ],
        },
      ]}
      gallery={{
        title: "Inside Our Sprinter Limousines",
        intro: "Actual cabins from the Atlanta fleet, starlight headliner, wet bar, and lounge seating.",
        images: [
          { src: galleryStarlight, alt: "Mercedes-Benz Sprinter Limo interior with blue starlight headliner in Atlanta", caption: "Starlight headliner with selectable LED accents" },
          { src: galleryBar, alt: "Wet bar inside an Atlanta Mercedes-Benz Sprinter Limousine", caption: "Wet bar with ice wells and stemware" },
          { src: galleryEntry, alt: "Low step-in entry of a Sprinter Limousine in Atlanta", caption: "Low step-in entry, easy in formalwear" },
        ],
      }}
      faqTitle="Sprinter Limo Atlanta FAQs"
      relatedTitle="Related Atlanta Limo & Chauffeur Services"
      faqs={faqs}
      related={[
        {
          to: "/chauffeur-service-atlanta",
          title: "Atlanta Chauffeur Service",
          desc: "Private chauffeurs for executives, weddings, and VIP travel across Atlanta.",
        },
        {
          to: "/atlanta-airport-car-service",
          title: "Atlanta Airport Car Service",
          desc: "Sprinter and SUV transfers from Hartsfield-Jackson and every Atlanta airport.",
        },
        {
          to: "/atlanta-private-airport-transfer",
          title: "Private Airport Transfer",
          desc: "Premium chauffeured transfers from every Atlanta private airport.",
        },
        {
          to: "/sprinter-van-service-atlanta",
          title: "Sprinter Van Service Atlanta",
          desc: "Chauffeured Sprinters for groups, executives and airport group transfers.",
        },
      ]}
      ctaTitle="Text Me About Your Evening"
      ctaBody="Text me your date, pickup, group size and stops. I'll check availability myself and send your custom quote in writing."
    />
  );
}
